import { Hidden } from "gql/graphql";
import { BoxedValue, Hidable } from "../model/types";

const BOXED_TYPENAMES = [
  "StringValue",
  "BooleanValue",
  "IntValue",
  "DecimalValue",
  "DateValue",
  "DateTimeValue",
];

function typenameOf(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null || !("__typename" in data)) {
    return undefined;
  }

  const { __typename } = data as { __typename?: unknown };

  return typeof __typename === "string" ? __typename : undefined;
}

function hiddenFlagOf(data: unknown): boolean {
  if (typeof data !== "object" || data === null || !("hidden" in data)) {
    return false;
  }

  return (data as { hidden?: unknown }).hidden === true;
}

/**
 * Скрыто ли значение защитой.
 *
 * Флаг `hidden` — запасной путь, а не равноправная проверка: он поднят и у типов со
 * своим смыслом слова «скрыто» (например, у события календаря, убранного из показа),
 * поэтому при известном `__typename` решает только он. Флаг остаётся для данных без
 * `__typename` — plain-JSON и моков, где различить случай больше нечем.
 */
export function isHidden<T>(data: Hidable<T>): data is Hidden {
  const typename = typenameOf(data);

  if (typename !== undefined) {
    return typename === "Hidden";
  }

  return hiddenFlagOf(data);
}

export function isBoxedValue<T>(data: Hidable<T>): data is T & BoxedValue {
  const typename = typenameOf(data);

  return typename !== undefined && BOXED_TYPENAMES.includes(typename);
}

/**
 * Имя скрытого объекта — для строковых мест вроде заголовка узла дерева или title,
 * куда плашку-компонент не подставить. Для видимого и пустого значения — пустая строка.
 */
export function instanceNameOf<T>(data: Hidable<T>): string {
  return isHidden(data) ? (data.instanceName ?? "") : "";
}

/**
 * Само значение, если оно видно; для скрытого и пустого — null. Нужен там, где значение
 * не рендерится, а участвует в логике: сравнение по id, подсчёты, defaultValues формы.
 */
export function valueOrNull<T>(data: Hidable<T>): T | null {
  if (data === null || data === undefined || isHidden(data)) {
    return null;
  }

  return data as T;
}
